import { useState, useMemo, useCallback, useContext } from 'react';
import { useField } from 'react-final-form';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useHelpDeskCategories from '@ubeya/shared/hooks/account/useHelpDeskCategories';
import useHelpDeskAdmins from '@ubeya/shared/hooks/account/useHelpDeskAdmins';
import { HELP_DESK_PRIORITY_LABEL_KEYS } from '@ubeya/shared/constants/helpDesk';
import { VISIBLE_PRIORITIES } from '../../../helpDeskConstants';

/**
 * Step-2 classification field logic: loads branch-scoped categories + admins, builds the
 * category/assignee/priority options, owns the "related to a shift" toggle, and resets the assignee
 * when the category changes — auto-assigning the category's default admin (autoAssignAdminId) when
 * one is configured, otherwise clearing it (it may not be valid for the new category).
 */
const useTicketClassificationFields = () => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();

  const { input: shiftIdInput } = useField('shiftId');
  const { input: shiftDetailsInput } = useField('shiftDetails');
  const { input: categoryInput } = useField('categoryId');
  const { input: assigneeInput } = useField('assignedAdminId');
  const { input: branchInput } = useField('branchId');
  const { input: employeeInput } = useField('employeeId');

  const [shiftRelated, setShiftRelated] = useState(() => !!shiftIdInput.value);

  const handleShiftToggle = useCallback(
    (next) => {
      setShiftRelated(next);
      if (!next) {
        shiftIdInput.onChange(null);
        shiftDetailsInput.onChange(null);
      }
    },
    [shiftIdInput, shiftDetailsInput]
  );

  const branchId = branchInput.value;
  const categoryId = categoryInput.value;

  const { categories, isLoading: categoriesLoading } = useHelpDeskCategories({
    accountId,
    branchId,
    onlyMyCategories: true
  });
  const { admins } = useHelpDeskAdmins({ accountId, branchId, categoryId });

  const categoriesById = useMemo(
    () => Object.fromEntries((categories || []).map((category) => [category.id, category])),
    [categories]
  );
  const adminsById = useMemo(() => Object.fromEntries((admins || []).map((admin) => [admin.id, admin])), [admins]);

  const categoryOptions = useMemo(
    () => (categories || []).map((category) => ({ value: category.id, label: category.name, category })),
    [categories]
  );

  const adminOptions = useMemo(
    () => [
      { value: null, label: t('help_desk_unassigned'), admin: null },
      ...(admins || []).map((admin) => ({ value: admin.id, label: admin.name, admin }))
    ],
    [admins, t]
  );

  const priorityOptions = useMemo(
    () =>
      VISIBLE_PRIORITIES.map((priority) => ({
        value: priority,
        label: t(HELP_DESK_PRIORITY_LABEL_KEYS[priority]),
        priority
      })),
    [t]
  );

  const handleCategoryChange = useCallback(
    (value) => {
      categoryInput.onChange(value);
      const nextCategory = categoriesById[value];
      assigneeInput.onChange(nextCategory?.autoAssignAdminId ?? null);
    },
    [categoryInput, assigneeInput, categoriesById]
  );

  return {
    branchId,
    employeeId: employeeInput.value,
    categoriesLoading,
    categoriesById,
    adminsById,
    categoryOptions,
    adminOptions,
    priorityOptions,
    shiftRelated,
    handleShiftToggle,
    handleCategoryChange
  };
};

export default useTicketClassificationFields;
